/**
 * Vivier de formation US — filtre « NCAA + » : NCAA, High School, Overtime Elite,
 * NBA Academy et PIT (Portsmouth Invitational Tournament, millésimé : PIT 2026,
 * PIT 2027, …).
 *
 * Un joueur en fait partie s'il est rattaché à une équipe dont le NIVEAU ou la
 * COMPÉTITION porte l'un de ces libellés (le OR est volontaire : l'organisation
 * niveau/compétition varie selon les clubs).
 *
 * ⚠️ À NE PAS confondre avec back/utils/ncaaUtils.js, qui reste STRICTEMENT NCAA :
 * il sert au « NCAA Commitment » (sélecteur + validation serveur). L'élargir
 * laisserait enregistrer un engagement NCAA vers une équipe High School.
 * ⚠️ Ni avec players.experienca_ncaa (expérience passée, onglet « Experiencia »).
 */

/**
 * Table NOMMÉE plutôt qu'une alternance illisible : on sait LAQUELLE a matché
 * (infobulle, debug) et on ajoute une ligne sans relire une regex de 120 caractères.
 * Aucun motif ne porte de drapeau `g` : `.test()` serait alors à état (lastIndex).
 */
export const PROSPECT_COMPETITIONS = [
  { code: 'ncaa', label: 'NCAA', pattern: /NCAA/i },
  { code: 'high_school', label: 'High School', pattern: /HIGH\s*SCHOOL/i },
  { code: 'ote', label: 'Overtime Elite', pattern: /OVERTIME\s*ELITE/i },
  // La locution ENTIÈRE : « NBA » seul (compétition existante, niveau USA) ne doit
  // pas être happé. ACADEM* couvre Academy comme Academia.
  { code: 'nba_academy', label: 'NBA Academy', pattern: /NBA\s*ACADEM/i },
  // Le millésime n'est PAS dans le motif : PIT 2026 devient PIT 2027 sans livraison.
  // \b protège « PITTSBURGH » (aucune frontière de mot après PIT), qui pullule en NCAA.
  { code: 'pit', label: 'PIT', pattern: /\bPIT\b/i },
];

/** Libellés couverts — alimente l'infobulle du filtre « NCAA + ». */
export const PROSPECT_COMPETITION_LABELS = PROSPECT_COMPETITIONS.map((c) => c.label);

// Rôles autorisés à utiliser le filtre « NCAA + » de la liste des joueurs.
const PROSPECT_FILTER_ROLES = ['super admin', 'admin pro'];

/** Renvoie le code de la compétition-vivier reconnue, ou null. */
export function prospectCompetitionCode(entry) {
  if (!entry) return null;
  const competition = entry.competition_nom || '';
  const niveau = entry.niveau_nom || '';
  const hit = PROSPECT_COMPETITIONS.find(
    (c) => c.pattern.test(competition) || c.pattern.test(niveau)
  );
  return hit ? hit.code : null;
}

export function isProspectCompetitionEntry(entry) {
  return prospectCompetitionCode(entry) !== null;
}

/**
 * Accepte aussi bien un joueur (`competitions_detail`, ex. GET /api/players) qu'une
 * ligne enrichie côté back (`player_competitions_detail` : documents, informes, intel,
 * medical, medidas, vidéos, news, technique).
 */
export function isProspectPlayer(playerOrRow) {
  const competitions =
    playerOrRow?.competitions_detail || playerOrRow?.player_competitions_detail;
  return Array.isArray(competitions) && competitions.some(isProspectCompetitionEntry);
}

export function canUseProspectFilter(role) {
  return PROSPECT_FILTER_ROLES.includes((role || '').toLowerCase());
}

/**
 * Variante « rôle courant » de canUseProspectFilter : le rôle est relu depuis
 * localStorage, seule source disponible DÈS le premier rendu (les états `userRole`
 * des écrans sont posés par un useEffect séparé, donc nuls au montage — un gate qui
 * en dépendrait laisserait passer une frame de contenu non filtré).
 * Le try/catch couvre un localStorage indisponible ou une valeur corrompue : dans le
 * doute on refuse, comme partout ailleurs sur ce filtre.
 */
export function currentUserCanUseProspectFilter() {
  try {
    return canUseProspectFilter(JSON.parse(localStorage.getItem('user') || 'null')?.role);
  } catch {
    return false;
  }
}
